import { defineStore } from 'pinia'

import {
  readonly,
  watch,
} from 'vue'
import { useSessionStorage } from '@vueuse/core'
import { isSettled } from '@nicolaudie-group/ng-ui-kit/helpers/isStatus'
import useAction from '@/composables/useAction'
import { buildDocumentationLink, buildRegaladeRequestLink, buildRegaladeTicketLink } from '@/helpers/profileRequests'
import * as sdk from '@/helpers/sdk'
import PbError from '@/classes/PbError.class'

const getValidateError = (expected) => new PbError({
  title: 'Unable to start session',
  message: `Invalid ${expected}`,
  page: true,
})

const validateNumber = (num, expected) => {
  if (typeof num === 'number' && !Number.isNaN(num)) {
    return num
  }
  throw getValidateError(expected)
}

const validateString = (str, expected) => {
  if (typeof str === 'string') {
    return decodeURIComponent(str)
  }
  throw getValidateError(expected)
}

const validateTruthyString = (str, expected) => {
  const result = validateString(str, expected)
  if (!result) throw getValidateError(expected)
  return result
}

function formatRequest(requestId, data) {
  const ticketId = String(validateNumber(data?.SRQ_TICKET_STI_ID, 'ticket ID'))
  const orderId = data?.SRQ_ORDER ?? null
  const documentationFilename = validateTruthyString(data?.SRQ_FILENAME, 'documentation filename')
  const orderStatus = validateString(data?.ORD_PAID, 'order status')

  return {
    requestId,
    ticketId,
    profileBrand: validateTruthyString(data?.SRQ_SSL_BRAND, 'profile brand'),
    profileName: validateTruthyString(data?.SRQ_SSL_NAME, 'profile name'),
    documentationFilename,
    requestAuthorEmail: validateTruthyString(data?.CTC_EMAIL, 'request author email'),
    attachedProfileUid: data?.SRQ_SSL_UID,
    documentationLink: buildDocumentationLink(documentationFilename),
    requestLink: buildRegaladeRequestLink(requestId),
    ticketLink: buildRegaladeTicketLink(ticketId),
    orderPaid: orderId ? orderStatus === 'PAID' : null,
  }
}

const useSupportStore = defineStore('support', () => {
  const sessionId = useSessionStorage('pb-support-session-request-id', null, {
    writeDefaults: false,
  })

  const {
    action: loadSession,
    data: session,
    status: sessionStatus,
  } = useAction(async () => {
    const requestId = sessionId.value

    if (!requestId) {
      return null
    }

    const result = (await sdk.v3.profileRequests.get({
      url: {
        requestId,
      },
    })).data

    return formatRequest(requestId, result)
  }, {
    shallow: true,
  })

  function leaveSession() {
    sessionId.value = null
    session.value = null
  }

  function joinSession(newRequestId) {
    leaveSession()
    const requestId = validateTruthyString(newRequestId, 'request ID')
    // Save the request ID to sessionStorage, to persist after refreshes
    sessionId.value = requestId
  }

  watch(sessionId, async (newRequestId, previousRequestId) => {
    // If there is no request ID
    if (!newRequestId) return

    // If the request ID did not change
    if (previousRequestId === newRequestId) return

    // If the request was not loaded at least once
    if (!isSettled(sessionStatus.value)) return

    // Get the session (request)
    await loadSession(newRequestId)
  })

  const {
    action: attachProfile,
  } = useAction(async (profileUid) => {
    if (!sessionId.value) return null

    const result = await sdk.v3.profileRequests.attachProfile({
      url: {
        profileUid,
        requestId: sessionId.value,
      },
    })

    return formatRequest(sessionId.value, result.data)
  }, { data: session })

  return {
    // Support session for profile requests
    sessionId: readonly(sessionId),
    session: readonly(session),
    sessionStatus: readonly(sessionStatus),
    joinSession,
    loadSession,
    leaveSession,
    attachProfile,
  }
})

export default useSupportStore
